import { useMemo } from 'react';
import { useAuth } from '@/lib/auth';
import { isChatOnlyAccess } from '@/lib/chat-only-access';
import { isTenantDeveloperUser } from '@/lib/developer-access';
import { filterVisibleNavigation } from '@/lib/navigation-visibility';
import type { NavigationCategory } from '@/lib/use-layout-config';

/**
 * Returns the filtered + sorted navigation categories that the current user
 * is allowed to see based on their role, grants, tenant modules, and WMS profile.
 *
 * @param warehouseProfile — optional raw warehouse profile string from LayoutConfig.
 *   When set, items with `wmsProfiles` not matching the active profile are hidden.
 */
export function useVisibleNavigation(
  navigation: NavigationCategory[],
  warehouseProfile?: string,
): NavigationCategory[] {
  const { user } = useAuth();

  return useMemo(() => {
    return filterVisibleNavigation(navigation, {
      role: user?.role,
      grants: user?.grants,
      enabledModules: user?.enabledModules,
      warehouseProfile,
      isAdminConsole: import.meta.env?.VITE_STRICT_NAV === 'true' || window.location.port === '3002' || window.location.hostname.startsWith('cc.'),
      isTenantDeveloper: isTenantDeveloperUser(user),
      isChatOnly: isChatOnlyAccess(user),
    });
  }, [navigation, user, warehouseProfile]);
}
